import { Dashboards, useDashboards } from 'adapter/in/dashboards/context/Dashboards';
import Dashboard from 'domain/entity/dashboard';
import { isNone, isSome } from 'fp-ts/Option';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GetDashboardsCriteriaBuilder } from 'use-case/dashboards/get/getDashboardsCriteria';

export const sortDashboardsByPinnedAndStarred = (dashboards: Dashboards): Dashboards =>
    [...dashboards].sort((a, b) => {
        if (a.pinned && !b.pinned) {
            return -1;
        }
        if (!a.pinned && b.pinned) {
            return 1;
        }
        if (a.starred && !b.starred) {
            return -1;
        }
        if (!a.starred && b.starred) {
            return 1;
        }
        return 0;
    });

export const useDashboardsContainer = (debouncedSearchTerm?: string) => {
    const [isSearching, setIsSearching] = useState(false);
    const [filteredDashboards, setFilteredDashboards] = useState<Dashboard[] | undefined>();
    const { get, getResult, getError, getIsLoading } = useDashboards();
    const [order, setOrder] = useState<{ columnKey: string; order: string } | undefined>({ columnKey: 'name', order: 'ascend' });

    useEffect(() => {
        if (isNone(getResult) && isNone(getError) && !getIsLoading) {
            get(GetDashboardsCriteriaBuilder.create().withSort(order?.columnKey).withOrder(order?.order).build());
        }
    }, [getResult, getError]);

    const dashboards = useMemo(() => {
        if (isSome(getResult)) return getResult.value;
        return undefined;
    }, [getResult]);

    useEffect(() => {
        if (dashboards && debouncedSearchTerm) {
            setIsSearching(true);
            setFilteredDashboards(
                sortDashboardsByPinnedAndStarred(
                    dashboards.filter((dash) => dash.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
                )
            );
            setIsSearching(false);
        }
        if (dashboards && (debouncedSearchTerm === '' || debouncedSearchTerm === undefined)) {
            setFilteredDashboards(sortDashboardsByPinnedAndStarred(dashboards));
        }
    }, [debouncedSearchTerm, dashboards]);

    const onTableChange = useCallback(
        (
            pagination: never,
            filters: never,
            sorter: { column?: { columnKey: string }; order?: 'ascend' | 'descend' },
            extra: { currentDataSource: []; action: 'paginate' | 'sort' | 'filter' }
        ) => {
            if (extra.action === 'sort') {
                get(GetDashboardsCriteriaBuilder.create().withSort(sorter.column?.columnKey).withOrder(sorter.order).build());
                setOrder(sorter.order ? { columnKey: sorter.column?.columnKey || '', order: sorter.order || '' } : undefined);
            }
        },
        []
    );

    return {
        dashboards: filteredDashboards,
        isLoading: isSearching || getIsLoading,
        hasError: isSome(getError),
        order,
        onTableChange,
    };
};
